import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Plus, Trash2, Star, X } from "lucide-react";
import {
  listAddresses,
  addAddress,
  updateAddress,
  removeAddress,
  setDefaultAddress,
  formatApiError,
} from "../lib/customerApi";

const EMPTY = {
  label: "Home",
  full_name: "",
  phone: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  pincode: "",
  country: "India",
  is_default: false,
};

export default function AddressesPane({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | {...address}

  const reload = () => {
    setLoading(true);
    listAddresses()
      .then(setItems)
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const save = async (form) => {
    try {
      if (form.id) await updateAddress(form.id, form);
      else await addAddress(form);
      setEditing(null);
      reload();
      toast.success(form.id ? "Address updated" : "Address saved");
    } catch (err) {
      toast.error(formatApiError(err, "Could not save address"));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this address?")) return;
    await removeAddress(id);
    reload();
    toast.success("Address removed");
  };

  const makeDefault = async (id) => {
    await setDefaultAddress(id);
    reload();
  };

  if (loading) return <p className="text-[#7A7A7A] text-sm">Loading…</p>;

  return (
    <div data-testid="account-addresses-pane">
      <div className="flex items-center justify-between mb-6">
        <p className="eyebrow">{items.length} saved {items.length === 1 ? "address" : "addresses"}</p>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          className="text-[11px] tracking-[0.22em] uppercase border border-[#1A1A1A] px-4 py-2.5 hover:bg-[#1A1A1A] hover:text-[#c3ba9e] flex items-center gap-2"
          data-testid="address-add"
        >
          <Plus size={13} /> Add address
        </button>
      </div>

      {items.length === 0 ? (
        <div className="border border-[#a89e80] p-16 text-center">
          <MapPin size={28} className="mx-auto text-[#4A4A4A]" />
          <p className="font-serif-display text-3xl mt-4">No saved addresses yet</p>
          <p className="text-[#7A7A7A] mt-2 text-sm">Save an address to check out faster next time.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-5">
          {items.map((a) => (
            <div key={a.id} className="border border-[#a89e80] p-5 relative" data-testid={`address-${a.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="eyebrow flex items-center gap-2">
                    {a.label || "Address"}
                    {a.is_default && (
                      <span className="bg-[#1A1A1A] text-[#c3ba9e] px-2 py-0.5 text-[9px] tracking-[0.22em]">Default</span>
                    )}
                  </p>
                  <p className="font-body text-[15px] mt-2">{a.full_name}</p>
                </div>
                <button
                  onClick={() => del(a.id)}
                  className="text-[#7A7A7A] hover:text-[#1A1A1A]"
                  aria-label="Delete address"
                  data-testid={`address-delete-${a.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              <p className="text-sm text-[#4A4A4A] mt-3 leading-relaxed">
                {a.address_line1}
                {a.address_line2 && <><br />{a.address_line2}</>}
                <br />
                {a.city}, {a.state} {a.pincode}
                <br />
                {a.country}
                <br />
                {a.phone}
              </p>
              <div className="flex items-center gap-3 mt-4 text-[11px] tracking-[0.22em] uppercase">
                <button onClick={() => setEditing(a)} className="text-[#1A1A1A] underline-offset-4 hover:underline" data-testid={`address-edit-${a.id}`}>
                  Edit
                </button>
                {!a.is_default && (
                  <button onClick={() => makeDefault(a.id)} className="text-[#7A7A7A] hover:text-[#1A1A1A] flex items-center gap-1" data-testid={`address-default-${a.id}`}>
                    <Star size={11} /> Make default
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <AddressForm initial={editing} onCancel={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function AddressForm({ initial, onCancel, onSave }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = (e) => {
    e.preventDefault();
    onSave(form);
  };
  return (
    <div className="fixed inset-0 z-[70] bg-[#1A1A1A]/60 backdrop-blur-sm flex items-center justify-center px-4 py-10" onClick={onCancel}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-[#c3ba9e] max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-[#1A1A1A]"
        data-testid="address-form"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#a89e80]">
          <h2 className="font-serif-display text-2xl">{form.id ? "Edit address" : "Add a new address"}</h2>
          <button type="button" onClick={onCancel} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-5 p-6">
          <Field label="Label">
            <input value={form.label} onChange={(e) => set("label", e.target.value)} className={inputCls} placeholder="Home / Office" />
          </Field>
          <Field label="Full name" required>
            <input required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Phone" required>
            <input required value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Pincode" required>
            <input required maxLength={6} value={form.pincode} onChange={(e) => set("pincode", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Address" required className="sm:col-span-2">
            <input required value={form.address_line1} onChange={(e) => set("address_line1", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Apt, suite (optional)" className="sm:col-span-2">
            <input value={form.address_line2} onChange={(e) => set("address_line2", e.target.value)} className={inputCls} />
          </Field>
          <Field label="City" required>
            <input required value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} />
          </Field>
          <Field label="State" required>
            <input required value={form.state} onChange={(e) => set("state", e.target.value)} className={inputCls} />
          </Field>
          <label className="sm:col-span-2 flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!form.is_default} onChange={(e) => set("is_default", e.target.checked)} className="accent-[#1A1A1A]" />
            Set as default shipping address
          </label>
        </div>
        <div className="flex justify-end gap-3 px-6 pb-6">
          <button type="button" onClick={onCancel} className="px-5 py-3 text-[11px] tracking-[0.22em] uppercase border border-[#1A1A1A]">
            Cancel
          </button>
          <button type="submit" className="px-6 py-3 text-[11px] tracking-[0.22em] uppercase bg-[#1A1A1A] text-[#c3ba9e]" data-testid="address-save">
            Save address
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls = "w-full border border-[#a89e80] bg-transparent px-3 py-2.5 text-[15px] outline-none focus:border-[#1A1A1A]";

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="eyebrow block mb-2">
        {label}
        {required && <span className="text-[#8c6c3c]"> *</span>}
      </span>
      {children}
    </label>
  );
}
